import atlas, { type IconOptions } from 'azure-maps-control'
import { mapIconRoadWorks } from '@/utils/map/map-icon-builder'
import DataSource = atlas.source.DataSource
import SymbolLayer = atlas.layer.SymbolLayer

export function createSymbolLayer(
  layerId: string,
  dataSource: DataSource,
  iconImage: string,
  color: string,
  anchor?:
    | 'center'
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
): { layer: SymbolLayer; clusterLayer: SymbolLayer } {
  const iconOptions: IconOptions = {
    allowOverlap: true,
    size: 1
  }

  if (anchor) {
    iconOptions.anchor = anchor
  }

  const layer = new atlas.layer.SymbolLayer(dataSource, layerId, {
    iconOptions: { ...iconOptions, image: `${iconImage}-${color}` },
    textOptions: {
      color: color,
      textField: ['get', 'text'],
      font: ['StandardFont-Regular'],
      offset: [0, 2]
    },
    filter: ['!', ['has', 'point_count']]
  })

  const clusterLayer = new atlas.layer.SymbolLayer(dataSource, `${layerId}-clustered`, {
    iconOptions: {
      ...iconOptions,
      image: `${iconImage}-${color === 'black' ? 'darkgrey' : color === 'red' ? 'darkred' : color}`
    },
    textOptions: {
      color: 'white',
      textField: ['get', 'point_count_abbreviated'],
      font: ['StandardFont-Bold'],
      size: 14,
      offset: iconImage === mapIconRoadWorks ? [0, 0.8] : [0, 0]
    },
    filter: ['has', 'point_count']
  })

  return { layer, clusterLayer }
}

export function createLineLayer(
  layerId: string,
  dataSource: DataSource,
  color: string,
  strokeWidth: number = 2,
  options?: { minZoom?: number; useZoomFilter?: boolean }
) {
  const layerOptions: atlas.LineLayerOptions = {
    strokeColor: color,
    strokeWidth: strokeWidth,
    strokeOpacity: 0.9,
    lineCap: 'butt',
    lineJoin: 'miter'
  }

  if (options?.minZoom !== undefined) {
    layerOptions.minZoom = options.minZoom
  }

  // Use zoom-based filter to progressively show more features as zoom increases
  // Features have _sampleGroup property (0-9), we show more groups at higher zoom
  // Matches original sampling rates: 60% -> 65% -> 70% -> 85% -> 100%
  if (options?.useZoomFilter) {
    layerOptions.filter = [
      'any',
      // Zoom >= 12: show all (100%)
      ['>=', ['zoom'], 12],
      // Zoom >= 10: show groups 0-8 (90%)
      ['all', ['>=', ['zoom'], 10], ['<', ['get', '_sampleGroup'], 9]],
      // Zoom >= 8: show groups 0-7 (80%)
      ['all', ['>=', ['zoom'], 8], ['<', ['get', '_sampleGroup'], 8]],
      // Zoom < 8: show groups 0-5 (60%)
      ['<', ['get', '_sampleGroup'], 6]
    ]
  }

  return new atlas.layer.LineLayer(dataSource, layerId, layerOptions)
}
